import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import TalentResults from './TalentResults';
import TalentSearchBar from './TalentSearchBar';
import TalentFilterToggle from './TalentFilterToggle';
import TalentFilters from './TalentFilters';
import TalentFiltersMobile from './TalentFiltersMobile';
import { Container, Box, Typography, useMediaQuery, IconButton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { IWorkProfile } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import MaskIcon from '../CustomSVGIcons/MaskIcon';
import MicIcon from '../CustomSVGIcons/MicIcon';
import DirectorIcon from '../CustomSVGIcons/DirectorIcon';
import CreatorIcon from '../CustomSVGIcons/CreatorIcon';

interface TypeTab {
    label: string;
    value: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const TYPE_TABS: TypeTab[] = [
    { label: 'Actors & Performers', value: 'performer', icon: MaskIcon },
    { label: 'Voiceover Artists', value: 'voiceover', icon: MicIcon },
    { label: 'Crew', value: 'technical', icon: DirectorIcon },
    { label: 'Content Creators', value: 'content_creator', icon: CreatorIcon },
];

interface IFindTalentsProps {
    talents: IWorkProfile[];
    filters?: any;
    onSearch: (searchValue: string) => void;
    onFilterChange: (filters: any) => void;
    customPagination?: JSX.Element;
    loading?: boolean;
    totalCount?: number;
    locationLabel?: string;
    onClearFilters?: () => void;
}

const FindTalentsContainer: React.FC<IFindTalentsProps> = ({
    talents,
    filters = {},
    onSearch,
    onFilterChange,
    customPagination,
    loading,
    totalCount,
    locationLabel,
    onClearFilters,
}) => {
    const [filterOpen, setFilterOpen] = useState(false);
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedType, setSelectedType] = useState(() => {
        const params = Object.fromEntries([...searchParams]);
        return params.profile_type || 'performer';
    });

    // Load other filters from URL search params on mount
    useEffect(() => {
        const params = Object.fromEntries([...searchParams]);

        // Load other filters from URL (excluding profile_type and search)
        const urlFilters: any = {};
        Object.keys(params).forEach(key => {
            if (key !== 'profile_type' && key !== 'search') {
                urlFilters[key] = params[key];
            }
        });

        if (Object.keys(urlFilters).length > 0) {
            onFilterChange({ ...filters, ...urlFilters });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Sync selectedType with URL changes
    useEffect(() => {
        const params = Object.fromEntries([...searchParams]);
        if (params.profile_type && params.profile_type !== selectedType) {
            setSelectedType(params.profile_type);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Remove selectedType from dependencies to prevent loops

    // Update URL when filters change
    const updateURLWithFilters = useCallback(
        (newFilters: any, newType?: string) => {
            const params = new URLSearchParams(searchParams);
            const type = newType || selectedType;

            // Update profile type
            if (type) {
                params.set('profile_type', type);
            }

            // Update other filters (excluding profile_type to avoid conflicts)
            Object.keys(newFilters).forEach(key => {
                if (
                    key !== 'profile_type' &&
                    newFilters[key] !== undefined &&
                    newFilters[key] !== null &&
                    newFilters[key] !== ''
                ) {
                    params.set(key, String(newFilters[key]));
                } else if (key !== 'profile_type') {
                    params.delete(key);
                }
            });

            setSearchParams(params);
        },
        [searchParams, setSearchParams, selectedType],
    );

    const handleTypeClick = (value: string) => {
        setSelectedType(value);
        const newFilters = { ...filters, profile_type: value };
        onFilterChange(newFilters);
        updateURLWithFilters(newFilters, value);
    };

    const handleFilterChange = (newFilters: any) => {
        // Ensure profile_type is preserved when filters change
        const filtersWithProfileType = { ...newFilters, profile_type: selectedType };
        onFilterChange(filtersWithProfileType);
        updateURLWithFilters(filtersWithProfileType);

        // Clear search if it's being reset or if only profile_type is provided (reset case)
        if (newFilters.search === '' || (Object.keys(newFilters).length === 1 && newFilters.profile_type)) {
            const params = new URLSearchParams(searchParams);
            params.delete('search');
            setSearchParams(params);
            onSearch('');
        }
    };

    const handleSearch = (searchValue: string) => {
        onSearch(searchValue);
    };

    const selectedTab = useMemo(() => {
        return TYPE_TABS.find(tab => tab.value === selectedType) || TYPE_TABS[0];
    }, [selectedType]);

    return (
        <Container
            disableGutters
            style={{
                height: 'auto',
                minHeight: '100dvh',
                justifyContent: 'flex-start',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
            }}
            sx={{
                width: '100%',
                margin: { xs: '0px', sm: '20px' },
                mb: 5,
                marginTop: { xs: '15px', sm: '10px', md: '15px', lg: '0px' },
                marginLeft: { xs: '0px', md: '65px' },
                marginRight: { xs: '0px', md: '65px' },
                paddingLeft: { xs: '0px', sm: '60px' },
                paddingRight: { xs: '0px', md: '60px' },
                overflowX: 'hidden',
                height: '70dvh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            maxWidth={false}>
            {/* Custom Type Icon Row */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    gap: 0,
                }}>
                {TYPE_TABS.map(tab => {
                    const selected = tab.value === selectedType;
                    return (
                        <Box
                            key={tab.value}
                            onClick={() => handleTypeClick(tab.value)}
                            tabIndex={0}
                            role="button"
                            aria-pressed={selected}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                px: 0,
                                py: isMobile ? 1 : 2,
                                borderRadius: 0,
                                bgcolor: selected ? theme[themeStyle].darkGray60 : 'none',
                                color: selected ? theme[themeStyle].highlight : theme[themeStyle].tag_white,
                                borderBottom: selected
                                    ? `3px solid ${theme[themeStyle].highlight}`
                                    : `1px solid ${theme[themeStyle].grey}`,
                                minWidth: 0,
                                minHeight: isMobile ? 60 : 90,
                                transition: 'all 0.2s',
                                outline: 'none',
                                '&:hover, &:focus': {
                                    bgcolor: theme[themeStyle].highlight + '33',
                                    color: theme[themeStyle].highlight,
                                    borderColor: theme[themeStyle].highlight,
                                },
                            }}>
                            <Box
                                sx={{
                                    fontSize: isMobile ? 28 : 40,
                                    mb: isMobile ? 0.5 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                {React.createElement(tab.icon, { fill: theme[themeStyle].tag_white })}
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? 12 : 16,
                                    fontWeight: selected ? 700 : 500,
                                    fontFamily: 'inherit',
                                    textAlign: 'center',
                                    color: 'inherit',
                                }}>
                                {tab.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            {/* Search and Filter Bar */}
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                    backgroundColor: theme[themeStyle].secondaryBg,
                    borderRadius: 10,
                    padding: 20,
                    maxWidth: '2000px',
                }}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    gap: 2,
                }}>
                <TalentSearchBar value={searchParams.get('searchTerm') || ''} onSearch={handleSearch} />
                {isMobile ? (
                    <IconButton
                        onClick={() => setMobileFilterOpen(true)}
                        sx={{
                            color: theme[themeStyle].highlight,
                            border: `1.5px solid ${theme[themeStyle].highlight}`,
                            borderRadius: 2,
                            p: 1,
                        }}>
                        <FilterListIcon />
                    </IconButton>
                ) : (
                    <TalentFilterToggle isOpen={filterOpen} onToggle={() => setFilterOpen(f => !f)} />
                )}
            </Container>
            <Container maxWidth="lgXxxl" disableGutters>
                {/* Count and Location */}
                <Box sx={{ width: '100%', mb: 2, display: 'flex', px: { xs: 1, sm: 2 }, justifyContent: 'flex-start' }}>
                    <Typography
                        sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: theme[themeStyle].tag_white,
                            textAlign: 'center',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '4px', // Optional: adds spacing between inline parts
                        }}>
                        <Typography
                            component="span"
                            sx={{ fontWeight: 'bold', display: 'inline', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {totalCount ? totalCount.toLocaleString() : '0'}
                        </Typography>
                        {selectedTab.label} across
                        <Typography
                            component="span"
                            sx={{ fontWeight: 'bold', display: 'inline', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {locationLabel ? locationLabel : 'All Locations'}
                        </Typography>
                    </Typography>
                </Box>

                {/* Filter Panel - Desktop */}
                {filterOpen && !isMobile && (
                    <TalentFilters filters={filters} onChange={handleFilterChange} profileType={selectedType as any} />
                )}

                {/* Mobile Filter Modal */}
                <TalentFiltersMobile
                    open={mobileFilterOpen}
                    onClose={() => setMobileFilterOpen(false)}
                    filters={filters}
                    onChange={handleFilterChange}
                    onResetFilter={onClearFilters}
                    profileType={selectedType as any}
                />

                <TalentResults
                    talents={talents}
                    loading={loading}
                    selectedCategory={selectedTab.label}
                    isMobile={isMobile}
                />
                {customPagination && customPagination}
            </Container>
        </Container>
    );
};

export default FindTalentsContainer;
